import React, { useState, useEffect } from 'react'
import { X, Settings, MessageSquare, DollarSign, Coins, Copy, Check } from 'lucide-react'
import { ConversationDetails } from '../types/conversationDetail'
import { adminApi } from '../services/AdminApiService'
import ConversationMessageView from './ConversationMessageView'
import './ConversationDetail.css'

interface ConversationDetailProps {
  phone: string
  onClose: () => void
}

interface ContentModalProps {
  title: string
  content: string
  onClose: () => void
}

const ContentModal: React.FC<ContentModalProps> = ({ title, content, onClose }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy content:', err)
    }
  }

  return (
    <div className="content-modal-overlay">
      <div className="content-modal">
        <div className="content-modal-header">
          <h3>{title}</h3>
          <div className="header-actions">
            <button 
              onClick={handleCopy}
              className={`copy-button ${copied ? 'copied' : ''}`}
              title="Copiar al portapapeles"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
            <button onClick={onClose} className="close-button">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="content-modal-body">
          <pre className="content-text">{content}</pre>
        </div>
      </div>
    </div>
  )
}

const ConversationDetail: React.FC<ConversationDetailProps> = ({ phone, onClose }) => {
  const [conversation, setConversation] = useState<ConversationDetails | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [contentModal, setContentModal] = useState<{ title: string; content: string } | null>(null)

  useEffect(() => {
    const fetchConversationDetails = async () => {
      try {
        setLoading(true)
        const response = await adminApi.getConversationDetails(phone)
        if (response.success) {
          setConversation(response.conversation)
        } else {
          setError('Error al cargar la conversación')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido')
      } finally {
        setLoading(false)
      }
    }

    fetchConversationDetails()
  }, [phone])

  const handleShowPrompt = (prompt: string) => {
    setContentModal({ title: 'Prompt Completo', content: prompt })
  }

  const handleShowMessages = (messages: string) => {
    let formattedMessages = messages
    try {
      const parsed = JSON.parse(messages)
      formattedMessages = JSON.stringify(parsed, null, 2)
    } catch {
      // Keep original if not valid JSON
    }
    setContentModal({ title: 'Messages JSON', content: formattedMessages })
  }

  const closeContentModal = () => {
    setContentModal(null)
  }

  const getDisplayName = () => {
    if (!conversation) return phone
    return conversation.name && conversation.lastname 
      ? `${conversation.name} ${conversation.lastname}` 
      : conversation.name || phone
  }

  const getTagColor = (tag: string): string => {
    const colors: Record<string, string> = {
      interested: '#22c55e',
      thinking: '#f59e0b',
      contracted: '#3b82f6',
      lost: '#ef4444',
      follow_up: '#8b5cf6',
      no_tag: '#6b7280'
    }
    return colors[tag] || '#6b7280'
  }

  const formatCurrency = (amount: number): string => {
    return `€${amount.toFixed(4)}`
  }

  if (loading) {
    return (
      <div className="conversation-detail-overlay">
        <div className="conversation-detail-modal">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando conversación...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !conversation) {
    return (
      <div className="conversation-detail-overlay">
        <div className="conversation-detail-modal">
          <div className="error-state">
            <p>Error: {error || 'Conversación no encontrada'}</p>
            <button onClick={onClose} className="close-button">Cerrar</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="conversation-detail-overlay">
        <div className="conversation-detail-modal">
          <div className="conversation-detail-header">
            <div className="header-info">
              <h2>{getDisplayName()}</h2>
              <span className="phone-number">{conversation.whatsappPhone}</span>
              <span 
                className="tag-badge"
                style={{ backgroundColor: getTagColor(conversation.tag) }}
              >
                {conversation.tag}
              </span>
            </div>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          <div className="conversation-detail-stats">
            <div className="stat-item">
              <MessageSquare size={16} />
              <span>{conversation.totalMessages} mensajes</span>
            </div>
            <div className="stat-item">
              <Coins size={16} />
              <span>{conversation.totalInputTokens + conversation.totalOutputTokens} tokens</span>
            </div>
            <div className="stat-item">
              <DollarSign size={16} />
              <span>{formatCurrency(conversation.totalCost)}</span>
            </div>
            <div className="stat-item">
              <Settings size={16} />
              <span>{conversation.models.join(', ')}</span>
            </div>
          </div>

          <div className="conversation-detail-messages">
            {conversation.messages.map((message) => (
              <ConversationMessageView
                key={message.id}
                message={message}
                onShowPrompt={handleShowPrompt}
                onShowMessages={handleShowMessages}
              />
            ))}
          </div>
        </div>
      </div>

      {contentModal && (
        <ContentModal
          title={contentModal.title}
          content={contentModal.content}
          onClose={closeContentModal}
        />
      )}
    </>
  )
}

export default ConversationDetail
